import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Globe, RefreshCw, Save, Check, X, AlertTriangle, ExternalLink,
  Plug, Search, Wifi, WifiOff, Sparkles, Loader, CheckCircle, XCircle, Filter,
  Activity, Heart, Zap, FileWarning, ArrowRight, Play,
} from "lucide-react";
import api from "../api/client";

export default function WordPressSync({ selectedSite }) {
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [siteUrl, setSiteUrl] = useState("");
  const [filter, setFilter] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [postTypes, setPostTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");

  // Bulk SEO generation state
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkConfig, setBulkConfig] = useState({ contentType: "both", skipExisting: true, dryRun: false });
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null); // { current, total, current_page, results, ... }
  const [bulkTaskId, setBulkTaskId] = useState(null);
  const [bulkDone, setBulkDone] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const bulkPollRef = useRef(null);

  // Tab state
  const [activeTab, setActiveTab] = useState("pages");

  // Issues tab state
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(null); // which fix_type is running
  const [insightsMsg, setInsightsMsg] = useState(null);

  // Health tab state
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const siteId = selectedSite?.id;

  // Fetch connection status
  const fetchStatus = useCallback(() => {
    if (!siteId) return;
    api.get(`/websites/${siteId}/wp-connector/status/`)
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, [siteId]);

  // Fetch available post types
  const fetchPostTypes = useCallback(() => {
    if (!siteId) return;
    api.get(`/websites/${siteId}/wp-connector/post-types/`)
      .then((r) => setPostTypes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setPostTypes([]));
  }, [siteId]);

  // Fetch pages from WP
  const fetchPages = useCallback(() => {
    if (!siteId) return;
    setSyncing(true);
    const params = typeFilter ? `?post_type=${typeFilter}` : "";
    api.get(`/websites/${siteId}/wp-connector/pages/${params}`)
      .then((r) => {
        setPages(r.data.pages || []);
        setEdits({});
        setSaveResult(null);
        fetchStatus();
      })
      .catch(() => {})
      .finally(() => setSyncing(false));
  }, [siteId, fetchStatus, typeFilter]);

  // Fetch insights (Issues tab)
  const fetchInsights = useCallback(() => {
    if (!siteId) return;
    setInsightsLoading(true);
    api.get(`/websites/${siteId}/bridge/insights/`)
      .then((r) => setInsights(r.data))
      .catch(() => setInsights(null))
      .finally(() => setInsightsLoading(false));
  }, [siteId]);

  // Fetch health (Health tab)
  const fetchHealth = useCallback(() => {
    if (!siteId) return;
    setHealthLoading(true);
    api.get(`/websites/${siteId}/bridge/health/`)
      .then((r) => setHealth(r.data))
      .catch(() => setHealth(null))
      .finally(() => setHealthLoading(false));
  }, [siteId]);

  // Trigger SEO scan
  const handleScan = () => {
    setScanning(true);
    setInsightsMsg(null);
    api.post(`/websites/${siteId}/bridge/insights/scan/`)
      .then((r) => {
        setInsightsMsg({ text: `Scan complete — ${r.data?.pages_analyzed || 0} pages analyzed`, type: "success" });
        fetchInsights();
      })
      .catch(() => setInsightsMsg({ text: "Scan failed", type: "error" }))
      .finally(() => setScanning(false));
  };

  // Run auto-fix
  const handleFix = (fixType) => {
    setFixing(fixType);
    setInsightsMsg(null);
    api.post(`/websites/${siteId}/bridge/insights/fix/`, { fix_type: fixType })
      .then((r) => {
        const fixed = r.data?.fixed || r.data?.updated || 0;
        setInsightsMsg({ text: `Fixed ${fixed} issues (${fixType})`, type: "success" });
        fetchInsights();
      })
      .catch(() => setInsightsMsg({ text: `Fix failed (${fixType})`, type: "error" }))
      .finally(() => setFixing(null));
  };

  // Reset state when switching sites
  useEffect(() => {
    setPages([]);
    setEdits({});
    setStatus(null);
    setSaveResult(null);
    setConnectError("");
    setApiKey("");
    setSiteUrl("");
    setFilter("");
    setTypeFilter("");
    setPostTypes([]);
    setInsights(null);
    setHealth(null);
    setActiveTab("pages");
    if (siteId) {
      setLoading(true);
      fetchStatus();
      fetchPostTypes();
      api.get(`/websites/${siteId}/wp-connector/pages/`)
        .then((r) => { setPages(r.data.pages || []); fetchStatus(); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [siteId, fetchStatus, fetchPostTypes]);

  // Re-fetch when type filter changes
  useEffect(() => {
    if (siteId && status?.connected) fetchPages();
  }, [typeFilter]); // eslint-disable-line

  // Fetch data when switching tabs
  useEffect(() => {
    if (!siteId || !isConnected) return;
    if (activeTab === "issues" && !insights && !insightsLoading) fetchInsights();
    if (activeTab === "health" && !health && !healthLoading) fetchHealth();
  }, [activeTab, siteId]); // eslint-disable-line

  // Connect
  const handleConnect = () => {
    if (!apiKey.trim()) { setConnectError("API key is required"); return; }
    setConnecting(true);
    setConnectError("");
    api.post(`/websites/${siteId}/wp-connector/connect/`, {
      api_key: apiKey.trim(),
      site_url: siteUrl.trim() || `https://${selectedSite.domain}`,
    })
      .then((r) => {
        setStatus({ connected: true, ...r.data });
        fetchPages();
        setApiKey("");
      })
      .catch((err) => {
        setConnectError(err.response?.data?.error || "Connection failed");
      })
      .finally(() => setConnecting(false));
  };

  // Edit cell
  const handleEdit = (pageId, field, value) => {
    setEdits((prev) => ({
      ...prev,
      [pageId]: { ...prev[pageId], [field]: value },
    }));
  };

  // Save all changes
  const handleSaveAll = () => {
    const changedPages = Object.entries(edits).map(([postId, fields]) => ({
      post_id: parseInt(postId),
      ...fields,
    }));
    if (changedPages.length === 0) return;

    setSaving(true);
    setSaveResult(null);
    api.post(`/websites/${siteId}/wp-connector/bulk-update/`, { pages: changedPages })
      .then((r) => {
        setSaveResult({ success: true, data: r.data });
        setEdits({});
        // Refresh pages to get updated scores
        setTimeout(fetchPages, 1000);
      })
      .catch((err) => {
        setSaveResult({ success: false, error: err.response?.data?.error || "Update failed" });
      })
      .finally(() => setSaving(false));
  };

  // Get current value (edited or original)
  const getValue = (page, field) => {
    if (edits[page.id] && edits[page.id][field] !== undefined) {
      return edits[page.id][field];
    }
    return page[field] || "";
  };

  // Bulk SEO generation
  const startBulkGeneration = () => {
    setBulkRunning(true);
    setBulkProgress(null);
    setBulkDone(false);
    setBulkError("");
    api.post(`/websites/${siteId}/seo/bulk-generate/`, {
      content_type: bulkConfig.contentType,
      skip_existing: bulkConfig.skipExisting,
      dry_run: bulkConfig.dryRun,
    })
      .then((r) => {
        setBulkTaskId(r.data.task_id);
        pollBulkStatus(r.data.task_id);
      })
      .catch((err) => {
        setBulkError(err.response?.data?.error || "Failed to start bulk generation");
        setBulkRunning(false);
      });
  };

  const pollBulkStatus = (taskId) => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    bulkPollRef.current = setInterval(() => {
      api.get(`/websites/${siteId}/seo/bulk-generate/status/?task_id=${taskId}`)
        .then((r) => {
          const d = r.data;
          if (d.state === "PROGRESS") {
            setBulkProgress(d.progress || d);
          } else if (d.state === "SUCCESS") {
            setBulkProgress(d.result || d.progress || d);
            setBulkDone(true);
            setBulkRunning(false);
            clearInterval(bulkPollRef.current);
            // Refresh the pages table
            fetchPages();
          } else if (d.state === "FAILURE") {
            setBulkError(d.error || "Task failed");
            setBulkRunning(false);
            clearInterval(bulkPollRef.current);
          }
        })
        .catch(() => {});
    }, 2000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { if (bulkPollRef.current) clearInterval(bulkPollRef.current); };
  }, []);

  const closeBulkModal = () => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    setBulkModal(false);
    setBulkRunning(false);
    setBulkProgress(null);
    setBulkTaskId(null);
    setBulkDone(false);
    setBulkError("");
    setBulkConfig({ contentType: "both", skipExisting: true, dryRun: false });
  };

  // Filter pages
  const filteredPages = pages.filter((p) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      (p.title || "").toLowerCase().includes(q) ||
      (p.slug || "").toLowerCase().includes(q) ||
      (p.focus_keyword || "").toLowerCase().includes(q)
    );
  });

  const editCount = Object.keys(edits).length;
  const isConnected = status?.connected && (status?.live_status === "online" || status?.api_key_set);

  if (!selectedSite) return <div className="text-gray-500 text-center py-20">Select a website</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Globe size={20} className="text-copper" /> WordPress Sync
        </h2>
        <div className="flex items-center gap-3">
          {status?.last_sync && (
            <span className="text-xs text-gray-500">
              Last sync: {new Date(status.last_sync).toLocaleString()}
            </span>
          )}
          {pages.length > 0 && (
            <span className="text-xs bg-dark-50 px-2 py-0.5 rounded text-gray-400">
              {pages.length} items
            </span>
          )}
          {isConnected && (
            <span className="flex items-center gap-1 text-xs text-green-400">
              <Wifi size={12} /> Connected
            </span>
          )}
        </div>
      </div>

      {/* Connect Panel — show if not connected */}
      {!isConnected && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
            <Plug size={16} className="text-copper" /> Connect WordPress Site
          </h3>
          <p className="text-xs text-gray-500 mb-4">
            Enter the API key from your WordPress site's OnesuiteSEO &rarr; API & Sync settings page.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Site URL</label>
              <input
                value={siteUrl}
                onChange={(e) => setSiteUrl(e.target.value)}
                placeholder={`https://${selectedSite.domain}`}
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">OnesuiteSEO API Key</label>
              <input
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste API key from WordPress"
                className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-copper/50"
              />
            </div>
          </div>
          {connectError && (
            <div className="text-xs text-red-400 mb-3 flex items-center gap-1">
              <AlertTriangle size={12} /> {connectError}
            </div>
          )}
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-copper text-dark rounded-lg font-medium hover:bg-copper/90 disabled:opacity-50"
          >
            {connecting ? <RefreshCw size={14} className="animate-spin" /> : <Plug size={14} />}
            {connecting ? "Connecting..." : "Test Connection"}
          </button>
        </div>
      )}

      {/* Tabs + Content — show when connected */}
      {isConnected && (
        <>
          {/* Tab Bar */}
          <div className="flex items-center gap-1 border-b border-dark-50">
            {[
              { key: "pages", label: "Pages", icon: Globe, count: pages.length },
              { key: "issues", label: "Issues", icon: FileWarning },
              { key: "health", label: "Health", icon: Heart },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? "border-copper text-copper"
                    : "border-transparent text-gray-500 hover:text-gray-300"
                }`}
              >
                <tab.icon size={14} />
                {tab.label}
                {tab.count != null && tab.count > 0 && (
                  <span className="text-[10px] bg-dark-50 px-1.5 py-0.5 rounded ml-1">{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Pages Tab */}
          {activeTab === "pages" && (
            <>
              {/* Toolbar */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                    <input
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      placeholder="Filter pages..."
                      className="bg-dark-300 border border-dark-50 rounded-lg pl-9 pr-3 py-2 text-sm w-52 focus:outline-none focus:border-copper/50"
                    />
                  </div>
                  {postTypes.length > 2 && (
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-2 text-sm text-gray-300 focus:outline-none focus:border-copper/50"
                    >
                      <option value="">All Types</option>
                      {postTypes.map((pt) => (
                        <option key={pt.name} value={pt.name}>
                          {pt.label} ({pt.count})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setBulkModal(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-copper/20 border border-copper/30 rounded-lg text-copper hover:bg-copper/30 font-medium"
                  >
                    <Sparkles size={12} /> Bulk Generate SEO
                  </button>
                  <button
                    onClick={fetchPages}
                    disabled={syncing}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-300 hover:text-white disabled:opacity-50"
                  >
                    <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> Sync Now
                  </button>
                  {editCount > 0 && (
                    <button
                      onClick={handleSaveAll}
                      disabled={saving}
                      className="flex items-center gap-1.5 px-4 py-1.5 text-xs bg-copper text-dark rounded-lg font-medium hover:bg-copper/90 disabled:opacity-50"
                    >
                      <Save size={12} /> {saving ? "Saving..." : `Save All Changes (${editCount})`}
                    </button>
                  )}
                </div>
              </div>

              {/* Save Result */}
              {saveResult && (
                <div className={`text-xs px-3 py-2 rounded-lg flex items-center gap-2 ${saveResult.success ? "bg-green-900/20 text-green-400" : "bg-red-900/20 text-red-400"}`}>
                  {saveResult.success ? <Check size={12} /> : <X size={12} />}
                  {saveResult.success
                    ? `Updated ${saveResult.data?.successes || 0} pages on WordPress`
                    : saveResult.error}
                </div>
              )}

              {/* Table */}
              {loading ? (
                <div className="py-12 text-center text-gray-500 animate-pulse">Loading pages from WordPress...</div>
              ) : pages.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  No pages found.{" "}
                  <button onClick={fetchPages} className="text-copper hover:underline">Sync now</button>
                </div>
              ) : (
                <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-xs text-gray-500 border-b border-dark-50">
                          <th className="text-left py-2.5 px-3 font-medium w-8">Score</th>
                          <th className="text-left py-2.5 px-3 font-medium">Title</th>
                          <th className="text-left py-2.5 px-3 font-medium w-16">Type</th>
                          <th className="text-left py-2.5 px-3 font-medium" style={{minWidth: "150px"}}>Focus Keyword</th>
                          <th className="text-left py-2.5 px-3 font-medium" style={{minWidth: "200px"}}>SEO Title</th>
                          <th className="text-left py-2.5 px-3 font-medium" style={{minWidth: "200px"}}>Meta Description</th>
                          <th className="py-2.5 px-3 font-medium w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPages.map((page) => {
                          const hasEdits = !!edits[page.id];
                          const missingKw = !getValue(page, "focus_keyword");
                          return (
                            <tr
                              key={page.id}
                              className={`border-b border-dark-50/50 hover:bg-dark-50/30 ${
                                hasEdits ? "bg-copper/5" : missingKw ? "bg-amber-900/10" : ""
                              }`}
                            >
                              <td className="py-2 px-3">
                                <ScoreBadge score={page.seo_score} />
                              </td>
                              <td className="py-2 px-3">
                                <div className="text-gray-200 text-sm leading-tight">{page.title}</div>
                                <div className="text-[10px] text-gray-600 font-mono truncate max-w-[250px]">/{page.slug}</div>
                              </td>
                              <td className="py-2 px-3">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                  page.type === "page" ? "bg-blue-900/20 text-blue-400"
                                  : page.type === "post" ? "bg-green-900/20 text-green-400"
                                  : "bg-purple-900/20 text-purple-400"
                                }`}>
                                  {page.type || "page"}
                                </span>
                              </td>
                              <td className="py-2 px-3">
                                <EditableCell
                                  value={getValue(page, "focus_keyword")}
                                  pageId={page.id}
                                  field="focus_keyword"
                                  placeholder="Set focus keyword..."
                                  onChange={handleEdit}
                                  isEditing={editingCell === `${page.id}-focus_keyword`}
                                  onEditStart={() => setEditingCell(`${page.id}-focus_keyword`)}
                                  onEditEnd={() => setEditingCell(null)}
                                  missing={missingKw}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <EditableCell
                                  value={getValue(page, "seo_title")}
                                  pageId={page.id}
                                  field="seo_title"
                                  placeholder="Set SEO title..."
                                  onChange={handleEdit}
                                  isEditing={editingCell === `${page.id}-seo_title`}
                                  onEditStart={() => setEditingCell(`${page.id}-seo_title`)}
                                  onEditEnd={() => setEditingCell(null)}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <EditableCell
                                  value={getValue(page, "seo_description")}
                                  pageId={page.id}
                                  field="meta_description"
                                  placeholder="Set meta description..."
                                  onChange={handleEdit}
                                  isEditing={editingCell === `${page.id}-meta_description`}
                                  onEditStart={() => setEditingCell(`${page.id}-meta_description`)}
                                  onEditEnd={() => setEditingCell(null)}
                                />
                              </td>
                              <td className="py-2 px-3">
                                <a
                                  href={page.permalink || page.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-gray-500 hover:text-copper"
                                >
                                  <ExternalLink size={13} />
                                </a>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Issues Tab */}
          {activeTab === "issues" && (
            <IssuesTab
              insights={insights}
              loading={insightsLoading}
              scanning={scanning}
              fixing={fixing}
              msg={insightsMsg}
              onScan={handleScan}
              onFix={handleFix}
              onRefresh={fetchInsights}
            />
          )}

          {/* Health Tab */}
          {activeTab === "health" && (
            <HealthTab
              health={health}
              loading={healthLoading}
              onRefresh={fetchHealth}
            />
          )}
        </>
      )}

      {/* Bulk Generate SEO Modal */}
      {bulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-dark-300 border border-dark-50 rounded-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-dark-50">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <Sparkles size={18} className="text-copper" /> Bulk Generate SEO
              </h3>
              <button onClick={closeBulkModal} className="text-gray-500 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-4">
              {/* Config (only before running) */}
              {!bulkRunning && !bulkDone && (
                <>
                  <p className="text-xs text-gray-400">
                    AI-generate SEO titles, meta descriptions, and focus keywords for all pages on{" "}
                    <span className="text-white font-medium">{selectedSite?.domain}</span>, then push directly to WordPress.
                  </p>

                  {/* Content Type */}
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">Content Type</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { key: "both", label: "All Content" },
                        { key: "pages", label: "Pages" },
                        { key: "posts", label: "Posts" },
                        ...postTypes
                          .filter((pt) => pt.name !== "page" && pt.name !== "post")
                          .map((pt) => ({ key: pt.name, label: `${pt.label} (${pt.count})` })),
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          onClick={() => setBulkConfig((c) => ({ ...c, contentType: opt.key }))}
                          className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                            bulkConfig.contentType === opt.key
                              ? "bg-copper/20 border-copper/40 text-copper"
                              : "bg-dark border-dark-50 text-gray-400 hover:text-white"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Options */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkConfig.skipExisting}
                        onChange={(e) => setBulkConfig((c) => ({ ...c, skipExisting: e.target.checked }))}
                        className="rounded bg-dark border-dark-50 text-copper focus:ring-copper/50"
                      />
                      Skip pages that already have SEO data
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bulkConfig.dryRun}
                        onChange={(e) => setBulkConfig((c) => ({ ...c, dryRun: e.target.checked }))}
                        className="rounded bg-dark border-dark-50 text-copper focus:ring-copper/50"
                      />
                      Dry run (generate only, don't push to WordPress)
                    </label>
                  </div>

                  {bulkError && (
                    <div className="text-xs text-red-400 flex items-center gap-1">
                      <AlertTriangle size={12} /> {bulkError}
                    </div>
                  )}
                </>
              )}

              {/* Progress */}
              {(bulkRunning || bulkDone) && bulkProgress && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300">
                      {bulkDone ? "Complete!" : `Processing ${bulkProgress.current || 0} of ${bulkProgress.total || "?"} pages...`}
                    </span>
                    {bulkRunning && <Loader size={14} className="animate-spin text-copper" />}
                    {bulkDone && <CheckCircle size={14} className="text-green-400" />}
                  </div>
                  <div className="w-full bg-dark-50 rounded-full h-2">
                    <div
                      className="bg-copper h-2 rounded-full transition-all"
                      style={{ width: `${bulkProgress.total ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%` }}
                    />
                  </div>
                  {bulkProgress.current_page && (
                    <div className="text-xs text-gray-500">Current: {bulkProgress.current_page}</div>
                  )}

                  {/* Summary stats */}
                  {bulkDone && (
                    <div className="grid grid-cols-4 gap-2 mt-2">
                      <div className="bg-dark/60 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-white">{bulkProgress.total || 0}</div>
                        <div className="text-[10px] text-gray-500">Total</div>
                      </div>
                      <div className="bg-dark/60 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-green-400">{bulkProgress.pushed || 0}</div>
                        <div className="text-[10px] text-gray-500">Pushed</div>
                      </div>
                      <div className="bg-dark/60 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-gray-400">{bulkProgress.skipped || 0}</div>
                        <div className="text-[10px] text-gray-500">Skipped</div>
                      </div>
                      <div className="bg-dark/60 rounded-lg p-2 text-center">
                        <div className="text-lg font-bold text-red-400">{bulkProgress.errors || 0}</div>
                        <div className="text-[10px] text-gray-500">Errors</div>
                      </div>
                    </div>
                  )}

                  {/* Results list */}
                  {bulkProgress.results && bulkProgress.results.length > 0 && (
                    <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                      {bulkProgress.results.map((r, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs py-1.5 border-b border-dark-50/50">
                          {r.status === "pushed" || r.status === "dry_run" ? (
                            <CheckCircle size={12} className="text-green-400 mt-0.5 shrink-0" />
                          ) : r.status === "skipped" ? (
                            <Check size={12} className="text-gray-500 mt-0.5 shrink-0" />
                          ) : (
                            <XCircle size={12} className="text-red-400 mt-0.5 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-gray-300 truncate">{r.title || r.url}</div>
                            {r.seo_title && (
                              <div className="text-gray-500 truncate">SEO: {r.seo_title}</div>
                            )}
                            {r.reason && <div className="text-gray-600 italic">{r.reason}</div>}
                            {r.error && <div className="text-red-400">{r.error}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-5 border-t border-dark-50 flex justify-end gap-2">
              {!bulkRunning && !bulkDone && (
                <>
                  <button onClick={closeBulkModal} className="px-4 py-2 text-xs text-gray-400 hover:text-white">
                    Cancel
                  </button>
                  <button
                    onClick={startBulkGeneration}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-copper text-dark rounded-lg font-semibold hover:bg-copper/90"
                  >
                    <Sparkles size={14} /> Generate & Push
                  </button>
                </>
              )}
              {bulkDone && (
                <button
                  onClick={closeBulkModal}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-copper text-dark rounded-lg font-semibold hover:bg-copper/90"
                >
                  <Check size={14} /> Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditableCell({ value, pageId, field, placeholder, onChange, isEditing, onEditStart, onEditEnd, missing }) {
  const [localVal, setLocalVal] = useState(value);

  useEffect(() => { setLocalVal(value); }, [value]);

  if (isEditing) {
    return (
      <input
        autoFocus
        value={localVal}
        onChange={(e) => setLocalVal(e.target.value)}
        onBlur={() => {
          if (localVal !== value) onChange(pageId, field, localVal);
          onEditEnd();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { if (localVal !== value) onChange(pageId, field, localVal); onEditEnd(); }
          if (e.key === "Escape") { setLocalVal(value); onEditEnd(); }
        }}
        className="w-full bg-dark border border-copper/50 rounded px-2 py-1 text-xs focus:outline-none"
      />
    );
  }

  return (
    <div
      onClick={onEditStart}
      className={`cursor-pointer px-2 py-1 rounded text-xs min-h-[24px] hover:bg-dark-50/50 transition-colors ${
        value ? "text-gray-300" : missing ? "text-amber-500/60 italic" : "text-gray-600 italic"
      }`}
    >
      {value || placeholder}
    </div>
  );
}

function ScoreBadge({ score }) {
  if (!score && score !== 0) return <span className="text-gray-600 text-xs">--</span>;
  const color = score >= 80 ? "text-green-400 bg-green-900/20" : score >= 50 ? "text-amber-400 bg-amber-900/20" : "text-red-400 bg-red-900/20";
  return (
    <span className={`inline-flex items-center justify-center w-8 h-6 rounded text-xs font-bold ${color}`}>
      {score}
    </span>
  );
}

function IssuesTab({ insights, loading, scanning, fixing, msg, onScan, onFix, onRefresh }) {
  if (loading) return <div className="py-12 text-center text-gray-500 animate-pulse">Loading insights...</div>;

  const summary = insights?.summary || insights;
  const issues = insights?.issues || insights?.top_issues || [];
  const pages = insights?.pages || [];

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={onScan}
            disabled={scanning}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-copper/20 border border-copper/30 rounded-lg text-copper hover:bg-copper/30 font-medium disabled:opacity-50"
          >
            {scanning ? <Loader size={12} className="animate-spin" /> : <Play size={12} />}
            {scanning ? "Scanning..." : "Run Scan"}
          </button>
          <button
            onClick={onRefresh}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-400 hover:text-white"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        {msg && (
          <span className={`text-xs px-3 py-1 rounded-lg ${msg.type === "error" ? "bg-red-900/20 text-red-400" : "bg-green-900/20 text-green-400"}`}>
            {msg.text}
          </span>
        )}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Pages Analyzed", value: summary.pages_analyzed, color: "text-blue-400" },
            { label: "Avg Score", value: summary.avg_score, color: summary.avg_score >= 70 ? "text-green-400" : "text-amber-400" },
            { label: "Good", value: summary.good, color: "text-green-400" },
            { label: "Needs Work", value: (summary.ok || 0) + (summary.bad || 0), color: "text-red-400" },
          ].map((s) => (
            <div key={s.label} className="bg-dark-300 border border-dark-50 rounded-xl p-4 text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.value ?? "--"}</div>
              <div className="text-xs text-gray-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Auto-Fix Buttons */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 mr-2">Auto-fix:</span>
        {["titles", "meta", "alt", "h1", "all"].map((t) => (
          <button
            key={t}
            onClick={() => onFix(t)}
            disabled={!!fixing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
          >
            {fixing === t ? <Loader size={10} className="animate-spin" /> : <Zap size={10} />}
            {t === "all" ? "Fix All" : `Fix ${t.charAt(0).toUpperCase() + t.slice(1)}`}
          </button>
        ))}
      </div>

      {/* Top Issues */}
      {issues.length > 0 && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dark-50 text-xs font-medium text-gray-400">Top Issues</div>
          <div className="divide-y divide-dark-50/50">
            {issues.map((issue, i) => (
              <div key={i} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileWarning size={14} className={issue.count > 10 ? "text-red-400" : "text-amber-400"} />
                  <span className="text-sm text-gray-300">{issue.issue || issue.label || issue}</span>
                </div>
                {issue.count != null && (
                  <span className="text-xs font-bold text-gray-400 bg-dark-50 px-2 py-0.5 rounded">{issue.count}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-Page Scores */}
      {pages.length > 0 && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dark-50 text-xs font-medium text-gray-400">Page Scores</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-dark-50">
                  <th className="text-left py-2 px-3 font-medium w-10">Score</th>
                  <th className="text-left py-2 px-3 font-medium">Page</th>
                  <th className="text-left py-2 px-3 font-medium w-48">Issues</th>
                </tr>
              </thead>
              <tbody>
                {pages.slice(0, 50).map((p, i) => (
                  <tr key={i} className="border-b border-dark-50/50 hover:bg-dark-50/30">
                    <td className="py-2 px-3"><ScoreBadge score={p.score} /></td>
                    <td className="py-2 px-3">
                      <div className="text-gray-300 text-sm">{p.title}</div>
                      <div className="text-[10px] text-gray-600 font-mono truncate max-w-[300px]">{p.url || p.slug}</div>
                    </td>
                    <td className="py-2 px-3">
                      {(p.issues || p.failed_checks || []).slice(0, 3).map((iss, j) => (
                        <span key={j} className="inline-block text-[10px] bg-red-900/20 text-red-400 px-1.5 py-0.5 rounded mr-1 mb-0.5">
                          {typeof iss === "string" ? iss : iss.label || iss.check}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!summary && !loading && (
        <div className="py-12 text-center text-gray-500">
          No insights data yet. Click <span className="text-copper">Run Scan</span> to analyze your site.
        </div>
      )}
    </div>
  );
}

function HealthTab({ health, loading, onRefresh }) {
  if (loading) return <div className="py-12 text-center text-gray-500 animate-pulse">Loading health data...</div>;

  if (!health) {
    return (
      <div className="py-12 text-center text-gray-500">
        Could not fetch health data.{" "}
        <button onClick={onRefresh} className="text-copper hover:underline">Retry</button>
      </div>
    );
  }

  const version = health.plugin_version || health.version || "--";
  const wpVersion = health.wp_version || "--";
  const phpVersion = health.php_version || "--";
  const siteUrl = health.site_url || health.url || "--";
  const postTypes = health.post_types || {};
  const stats404 = health["404_stats"] || health.four_oh_four_stats || {};
  const redirectCount = health.redirect_count ?? "--";
  const insightsSummary = health.insights_summary || {};
  const status = health.status || "unknown";

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex justify-end">
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-dark-300 border border-dark-50 rounded-lg text-gray-400 hover:text-white"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Status Banner */}
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${
        status === "ok" || status === "connected" ? "bg-green-900/10 border-green-800/30" : "bg-amber-900/10 border-amber-800/30"
      }`}>
        <Heart size={18} className={status === "ok" || status === "connected" ? "text-green-400" : "text-amber-400"} />
        <div>
          <div className="text-sm font-medium text-white">
            {status === "ok" || status === "connected" ? "Plugin Online" : `Status: ${status}`}
          </div>
          <div className="text-xs text-gray-500">{siteUrl}</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs text-gray-400">Plugin v{version}</div>
          <div className="text-[10px] text-gray-600">WP {wpVersion} / PHP {phpVersion}</div>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Redirects", value: redirectCount, color: "text-blue-400" },
          { label: "404s (Unresolved)", value: stats404.unresolved ?? "--", color: "text-red-400" },
          { label: "404s (Total)", value: stats404.total ?? "--", color: "text-gray-400" },
          { label: "Avg SEO Score", value: insightsSummary.avg_score ?? "--", color: "text-green-400" },
        ].map((s) => (
          <div key={s.label} className="bg-dark-300 border border-dark-50 rounded-xl p-4 text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Post Type Counts */}
      {Object.keys(postTypes).length > 0 && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dark-50 text-xs font-medium text-gray-400">Content Counts</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-dark-50">
            {Object.entries(postTypes).map(([type, count]) => (
              <div key={type} className="bg-dark-300 px-4 py-3 text-center">
                <div className="text-lg font-bold text-white">{count}</div>
                <div className="text-xs text-gray-500 capitalize">{type.replace(/_/g, " ")}s</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
